// Lightweight client-side PDF export utility for dashboard / team-card screenshots.
// Captures a DOM node with html2canvas, then composes a multi-page A4 PDF via jsPDF.
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 8;

// ---------------- shared page chrome helpers ----------------

const drawHeader = (pdf, { pageW, title, subtitle, pageNum, totalPages }) => {
  const HEADER_H = 18;
  pdf.setFillColor(123, 31, 47); // MPL maroon
  pdf.rect(0, 0, pageW, HEADER_H, 'F');
  pdf.setFontSize(13);
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.text(title, MARGIN_MM, 9);
  if (subtitle) {
    pdf.setFontSize(8.5);
    pdf.setFont('helvetica', 'normal');
    pdf.text(subtitle, MARGIN_MM, 14);
  }
  const stamp = new Date().toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  pdf.setFontSize(8);
  pdf.text(`Generated ${stamp}`, pageW - MARGIN_MM, 9, { align: 'right' });
  pdf.text(`Page ${pageNum} of ${totalPages}`, pageW - MARGIN_MM, 14, { align: 'right' });
};

const drawFooter = (pdf, { pageW, pageH }) => {
  pdf.setTextColor(120, 120, 120);
  pdf.setFontSize(7.5);
  pdf.setFont('helvetica', 'italic');
  pdf.text(
    'Confidential — for internal use only.',
    pageW / 2,
    pageH - MARGIN_MM / 2,
    { align: 'center' }
  );
};

/**
 * Capture the DOM node and render into a multi-page PDF.
 * @param {HTMLElement} node — the element to capture
 * @param {Object} opts
 *   filename: string  — output file name (no extension)
 *   title:    string  — header title printed on every page
 *   subtitle: string  — second line under the title (optional)
 *   orientation: 'portrait' | 'landscape' (default: portrait)
 */
export async function exportNodeToPdf(node, opts = {}) {
  if (!node) throw new Error('No node to export');
  const {
    filename = 'export',
    title = 'MPL Finance Report',
    subtitle = '',
    orientation = 'portrait',
  } = opts;

  // Apply the print-friendly tweaks ONLY to the cloned document (via
  // html2canvas onclone). Mutating the live <body> with mm/cm units can
  // collapse grid tracks to 0 inside the offscreen clone iframe and
  // produce a blank canvas — keep the live DOM untouched.
  const canvas = await html2canvas(node, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    windowWidth: node.scrollWidth,
    onclone: (clonedDoc) => {
      clonedDoc.body.classList.add('pdf-exporting');
    },
  });

  const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  const pageW = orientation === 'landscape' ? A4_HEIGHT_MM : A4_WIDTH_MM;
  const pageH = orientation === 'landscape' ? A4_WIDTH_MM : A4_HEIGHT_MM;

  const HEADER_H = 18;
  const FOOTER_H = 8;
  const usableW = pageW - 2 * MARGIN_MM;
  const usableH = pageH - HEADER_H - FOOTER_H - MARGIN_MM;

  const imgWmm = usableW;
  const imgHmm = (canvas.height * imgWmm) / canvas.width;
  const totalPages = Math.max(1, Math.ceil(imgHmm / usableH));
  const sliceCanvasH = Math.floor((usableH / imgHmm) * canvas.height);

  for (let i = 0; i < totalPages; i++) {
    if (i > 0) pdf.addPage();
    drawHeader(pdf, { pageW, title, subtitle, pageNum: i + 1, totalPages });

    const sliceCanvas = document.createElement('canvas');
    const sliceY = i * sliceCanvasH;
    const sliceH = Math.min(sliceCanvasH, canvas.height - sliceY);
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceH;
    const ctx = sliceCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(canvas, 0, sliceY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.85);
    const sliceImgHmm = (sliceH * imgWmm) / canvas.width;
    pdf.addImage(
      sliceImg,
      'JPEG',
      MARGIN_MM,
      HEADER_H + MARGIN_MM / 2,
      imgWmm,
      sliceImgHmm,
      undefined,
      'FAST'
    );

    drawFooter(pdf, { pageW, pageH });
  }

  pdf.save(`${filename}.pdf`);
}

/**
 * Export a list of card elements as a paginated PDF where each card is
 * atomic — no card is ever split across page boundaries. Cards are laid
 * out in a 2-column grid in landscape A4; when a card doesn't fit in the
 * remaining vertical space of its column, it flows to the next page.
 *
 * @param {HTMLElement[]} cards — the list of card DOM nodes to render
 * @param {Object} opts
 *   filename: string
 *   title:    string
 *   subtitle: string
 *   columns:  number  — 1 or 2 (default 2)
 */
export async function exportCardsToPdf(cards, opts = {}) {
  if (!cards || cards.length === 0) throw new Error('No cards to export');
  const {
    filename = 'export',
    title = 'MPL Finance Report',
    subtitle = '',
    columns = 2,
  } = opts;

  // Render each card individually so html2canvas can't split it across pages.
  // We also inject the .pdf-exporting class into the cloned doc so hover chrome
  // and the "ignore" buttons disappear from the card capture.
  //
  // NOTE: do NOT pass `windowWidth: card.scrollWidth` here. Each card is just a
  // single grid child; setting the clone iframe to the card's own width collapses
  // the surrounding layout context (sidebar + gutters) and stretches the capture
  // into a tall, narrow image. Using the default (current window width) keeps
  // the card's on-screen proportions intact in the capture.
  const rendered = [];
  for (const card of cards) {
    // eslint-disable-next-line no-await-in-loop
    const c = await html2canvas(card, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      onclone: (clonedDoc) => {
        clonedDoc.body.classList.add('pdf-exporting');
      },
    });
    rendered.push(c);
  }

  // ---- PDF layout ----
  const orientation = 'landscape';
  const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
  const pageW = A4_HEIGHT_MM; // 297 (landscape)
  const pageH = A4_WIDTH_MM;  // 210 (landscape)

  const HEADER_H = 18;
  const FOOTER_H = 8;
  const GAP_MM = 5;

  const usableW = pageW - 2 * MARGIN_MM;
  const usableH = pageH - HEADER_H - FOOTER_H - MARGIN_MM;

  const cols = Math.max(1, Math.min(2, columns));
  const colW = (usableW - GAP_MM * (cols - 1)) / cols;

  // Pre-compute the rendered mm-height of each card when scaled to colW.
  // Clamp any single oversize card so it fits on a single page height.
  const placements = rendered.map((c) => {
    const natH = (c.height * colW) / c.width;
    const h = Math.min(natH, usableH);
    return { canvas: c, h };
  });

  // Two-column page packer: each column has a running y offset. We drop the
  // next card into whichever column has more room; if neither column can fit
  // the card, we start a new page.
  const contentTop = HEADER_H + MARGIN_MM / 2;
  const pages = [];
  let colY = Array(cols).fill(0);
  let pageItems = [];

  const pushPage = () => {
    if (pageItems.length) pages.push(pageItems);
    pageItems = [];
    colY = Array(cols).fill(0);
  };

  for (const p of placements) {
    // Pick the column with the least used height
    let bestCol = 0;
    for (let i = 1; i < cols; i++) {
      if (colY[i] < colY[bestCol]) bestCol = i;
    }
    const remaining = usableH - colY[bestCol];
    if (p.h > remaining + 0.01) {
      // Doesn't fit anywhere — new page
      pushPage();
      bestCol = 0;
    }
    const x = MARGIN_MM + bestCol * (colW + GAP_MM);
    const y = contentTop + colY[bestCol];
    pageItems.push({ canvas: p.canvas, x, y, w: colW, h: p.h });
    colY[bestCol] += p.h + GAP_MM;
  }
  pushPage();

  const totalPages = Math.max(1, pages.length);
  for (let i = 0; i < totalPages; i++) {
    if (i > 0) pdf.addPage();
    drawHeader(pdf, { pageW, title, subtitle, pageNum: i + 1, totalPages });
    const items = pages[i] || [];
    for (const it of items) {
      const img = it.canvas.toDataURL('image/jpeg', 0.88);
      pdf.addImage(img, 'JPEG', it.x, it.y, it.w, it.h, undefined, 'FAST');
    }
    drawFooter(pdf, { pageW, pageH });
  }

  pdf.save(`${filename}.pdf`);
}
